import { Close, Add, Delete } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname } from "../../../utils";
import {
  SERVICE_PACKAGE_TYPE_ONBOARDING,
  SERVICE_PACKAGE_TYPE_SETUP,
  SERVICE_PACKAGE_TYPE_MARKETING,
  SERVICE_PACKAGE_TYPE_TRAINING,
  SERVICE_PACKAGE_TYPE_CUSTOM,
  SERVICE_PACKAGE_TYPE_LABELS,
  SERVICE_PACKAGE_BILLING_TYPE_ONE_TIME,
  SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION,
  SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY,
  SERVICE_PACKAGE_BILLING_INTERVAL_ANNUAL,
} from "../../../constants";

interface ServicePackage {
  id: string;
  name: string;
  description?: string;
  shortDescription?: string;
  packageType: string;
  price: number;
  currency: string;
  displayOrder: number;
  isActive: boolean;
  isFeatured: boolean;
  iconName?: string;
  featuresIncluded?: string[];
  billingType?: string;
  billingInterval?: string;
}

interface ServicePackageEditorDialogProps {
  open: boolean;
  onClose: () => void;
  package: ServicePackage | null;
  whitelabelId: string;
  getToken: () => Promise<string>;
  onSave: () => void;
}

const PACKAGE_TYPES = [
  SERVICE_PACKAGE_TYPE_ONBOARDING,
  SERVICE_PACKAGE_TYPE_SETUP,
  SERVICE_PACKAGE_TYPE_MARKETING,
  SERVICE_PACKAGE_TYPE_TRAINING,
  SERVICE_PACKAGE_TYPE_CUSTOM,
];

export default function ServicePackageEditorDialog({
  open,
  onClose,
  package: existingPackage,
  whitelabelId,
  getToken,
  onSave,
}: ServicePackageEditorDialogProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [packageType, setPackageType] = useState(SERVICE_PACKAGE_TYPE_ONBOARDING);
  const [price, setPrice] = useState<number>(0);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);
  const [featuresIncluded, setFeaturesIncluded] = useState<string[]>([]);
  const [newFeature, setNewFeature] = useState("");
  const [billingType, setBillingType] = useState(SERVICE_PACKAGE_BILLING_TYPE_ONE_TIME);
  const [billingInterval, setBillingInterval] = useState(SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY);

  const isEditMode = !!existingPackage;

  useEffect(() => {
    if (existingPackage) {
      setName(existingPackage.name);
      setDescription(existingPackage.description || "");
      setShortDescription(existingPackage.shortDescription || "");
      setPackageType(existingPackage.packageType);
      setPrice(existingPackage.price);
      setDisplayOrder(existingPackage.displayOrder || 0);
      setIsActive(existingPackage.isActive);
      setIsFeatured(existingPackage.isFeatured);
      setFeaturesIncluded(existingPackage.featuresIncluded || []);
      setBillingType(existingPackage.billingType || SERVICE_PACKAGE_BILLING_TYPE_ONE_TIME);
      setBillingInterval(existingPackage.billingInterval || SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY);
    } else {
      // Reset form for new package
      setName("");
      setDescription("");
      setShortDescription("");
      setPackageType(SERVICE_PACKAGE_TYPE_ONBOARDING);
      setPrice(0);
      setDisplayOrder(0);
      setIsActive(true);
      setIsFeatured(false);
      setFeaturesIncluded([]);
      setBillingType(SERVICE_PACKAGE_BILLING_TYPE_ONE_TIME);
      setBillingInterval(SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY);
    }
    setError(null);
    setNewFeature("");
  }, [existingPackage, open]);

  const handleAddFeature = () => {
    if (newFeature.trim() && !featuresIncluded.includes(newFeature.trim())) {
      setFeaturesIncluded([...featuresIncluded, newFeature.trim()]);
      setNewFeature("");
    }
  };

  const handleRemoveFeature = (index: number) => {
    setFeaturesIncluded(featuresIncluded.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Package name is required");
      return;
    }
    if (price < 0) {
      setError("Price must be a positive number");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const token = await getToken();
      const packageData = {
        name: name.trim(),
        description: description.trim() || null,
        shortDescription: shortDescription.trim() || null,
        packageType,
        price,
        displayOrder,
        isActive,
        isFeatured,
        featuresIncluded: featuresIncluded.length > 0 ? featuresIncluded : null,
        billingType,
        billingInterval: billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION ? billingInterval : null,
      };

      if (isEditMode && existingPackage) {
        await axios.put(
          `${hostname}/whitelabel/${whitelabelId}/service-packages/${existingPackage.id}`,
          packageData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${hostname}/whitelabel/${whitelabelId}/service-packages`,
          packageData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      onSave();
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          `Failed to ${isEditMode ? "update" : "create"} service package`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          {isEditMode ? "Edit Service Package" : "Create Service Package"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Package Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              placeholder="e.g., Premium Onboarding"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Package Type"
              value={packageType}
              onChange={(e) => setPackageType(e.target.value)}
              fullWidth
              required
            >
              {PACKAGE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {SERVICE_PACKAGE_TYPE_LABELS[type] || type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Price"
              type="number"
              value={price}
              onChange={(e) => setPrice(Number(e.target.value))}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Billing Type"
              value={billingType}
              onChange={(e) => setBillingType(e.target.value)}
              fullWidth
              required
            >
              <MenuItem value={SERVICE_PACKAGE_BILLING_TYPE_ONE_TIME}>
                One-time payment
              </MenuItem>
              <MenuItem value={SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION}>
                Recurring subscription
              </MenuItem>
            </TextField>
          </Grid>

          {billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION && (
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Billing Interval"
                value={billingInterval}
                onChange={(e) => setBillingInterval(e.target.value)}
                fullWidth
                required
              >
                <MenuItem value={SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY}>
                  Monthly
                </MenuItem>
                <MenuItem value={SERVICE_PACKAGE_BILLING_INTERVAL_ANNUAL}>
                  Annual
                </MenuItem>
              </TextField>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label="Short Description"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              fullWidth
              placeholder="Brief description shown in checkout (max 500 chars)"
              inputProps={{ maxLength: 500 }}
              helperText={`${shortDescription.length}/500`}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Full Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Detailed description of what's included"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Display Order"
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(Number(e.target.value))}
              fullWidth
              helperText="Lower numbers appear first"
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", gap: 2, pt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isFeatured}
                    onChange={(e) => setIsFeatured(e.target.checked)}
                  />
                }
                label="Featured"
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Features Included
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <TextField
                size="small"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                placeholder="Add a feature..."
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddFeature();
                  }
                }}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddFeature}
                disabled={!newFeature.trim()}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {featuresIncluded.map((feature, index) => (
                <Chip
                  key={index}
                  label={feature}
                  onDelete={() => handleRemoveFeature(index)}
                  deleteIcon={<Delete />}
                />
              ))}
              {featuresIncluded.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No features added yet. Add features to show what's included in
                  this package.
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSave}
          loading={saving}
          variant="contained"
        >
          {isEditMode ? "Save Changes" : "Create Package"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
